'use client'

import React from 'react'
import { StarIcon } from 'lucide-react'

// Animation container component
function MarqueeContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex w-full overflow-hidden">
      <div className="animate-marquee flex min-w-full shrink-0 items-center justify-around gap-1 py-2">
        {children}
      </div>
      <div className="animate-marquee flex min-w-full shrink-0 items-center justify-around gap-1 py-2">
        {children}
      </div>
    </div>
  )
}

// Testimonial item component
interface TestimonialProps {
  name: string
  title: string
  message: string
  rating: number
}

function Testimonial({ name, title, message, rating }: TestimonialProps) {
  return (
    <div className="flex h-full w-72 flex-col rounded-xl bg-[#100D1C] border border-gray-800 p-6 shadow-md shadow-[#8A6FE8]/10">
      {/* Stars */}
      <div className="mb-2 flex">
        {Array.from({ length: 5 }).map((_, i) => (
          <StarIcon
            key={i}
            className={`h-4 w-4 ${
              i < rating ? 'fill-[#8A6FE8] text-[#8A6FE8]' : 'fill-gray-700 text-gray-700'
            }`}
          />
        ))}
      </div>
      
      {/* Message */}
      <p className="mb-4 flex-1 text-sm text-gray-300">{message}</p>
      
      {/* Author */}
      <div>
        <p className="font-medium text-white">{name}</p>
        <p className="text-xs text-gray-400">{title}</p>
      </div>
    </div>
  )
}

export function TestimonialsMarquee() {
  // Testimonial data
  const testimonials = [
    {
      name: 'Sarah K.',
      title: 'Marketing Manager',
      message: 'This service has transformed how we approach PR campaigns. The sentiment analysis feature is a game-changer!',
      rating: 5,
    },
    {
      name: 'John D.',
      title: 'CEO',
      message: 'Real-time updates helped us respond quickly during a crisis. Highly recommend!',
      rating: 5,
    },
    {
      name: 'Michael T.',
      title: 'PR Director',
      message: 'The competitor tracking feature gives us valuable insights we never had before. Worth every penny.',
      rating: 4,
    },
    {
      name: 'Emily R.',
      title: 'Brand Manager',
      message: "We've increased our positive media mentions by 40% since using this platform. Amazing results!",
      rating: 5,
    },
    {
      name: 'David W.',
      title: 'Communications Specialist',
      message: 'The custom reports make it easy to share insights with our executive team. Very intuitive platform.',
      rating: 4,
    },
  ]

  // Create two groups for alternating directions
  const group1 = testimonials.slice(0, 3)
  const group2 = testimonials.slice(3).concat(testimonials.slice(0, 1))

  return (
    <div className="mx-auto max-w-screen-xl">
      <div className="space-y-2">
        {/* First row - left to right */}
        <MarqueeContainer>
          {group1.map((testimonial, i) => (
            <div key={i} className="mx-0.5">
              <Testimonial {...testimonial} />
            </div>
          ))}
        </MarqueeContainer>
        
        {/* Second row - right to left */}
        <div className="relative flex w-full overflow-hidden">
          <div className="animate-marquee-reverse flex min-w-full shrink-0 items-center justify-around gap-1 py-2">
            {group2.map((testimonial, i) => (
              <div key={i} className="mx-0.5">
                <Testimonial {...testimonial} />
              </div>
            ))}
          </div>
          <div className="animate-marquee-reverse flex min-w-full shrink-0 items-center justify-around gap-1 py-2">
            {group2.map((testimonial, i) => (
              <div key={i} className="mx-0.5">
                <Testimonial {...testimonial} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
} 